import React, { useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Target, Eye, Shield } from 'lucide-react';
import Footer from '../components/Footer';
import PageTitle from '../components/PageTitle';

const About = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);
  return (
    <div className="bg-black text-white min-h-screen">
      <PageTitle title="About - Our Story & Mission" />
      {/* BLOCK 1 - HERO */}
      <section className="pt-32 pb-24 lg:pt-40 lg:pb-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full animate-wave" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="abstract-lines" width="100" height="100" patternUnits="userSpaceOnUse">
                <path d="M 0 50 Q 25 25, 50 50 T 100 50" fill="none" stroke="#06b6d4" strokeWidth="1"/>
                <path d="M 0 70 Q 25 45, 50 70 T 100 70" fill="none" stroke="#06b6d4" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#abstract-lines)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center relative z-10">
          <h1 className="text-6xl lg:text-8xl font-bold mb-8 reveal-on-scroll animate-fadeUp">
            A Future Where Identity Endures
          </h1>
          <p className="text-2xl lg:text-3xl text-cyan-400 font-light max-w-4xl mx-auto reveal-on-scroll">
            RepresenX exists to preserve what makes you, you.
          </p>
        </div>
      </section>

      {/* BLOCK 2 - STORY */}
      <section className="py-24 lg:py-32 bg-[#0B1F2E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-16">
            <div className="lg:col-span-4 reveal-on-scroll animate-slide-in-left">
              <h2 className="text-4xl lg:text-5xl font-bold">Our Story</h2>
            </div>
            <div className="lg:col-span-8 reveal-on-scroll animate-slide-in-right">
              <p className="text-xl text-gray-300 leading-relaxed">
                RepresenX was founded on a belief that identity is more than data — it's values, 
                emotions, and the ways we shape others. Our mission is to extend that presence 
                meaningfully through technology. We recognized that while memories fade and moments 
                pass, the essence of who we are — our principles, our voice, our unique way of 
                seeing the world — deserves to endure. RepresenX is the bridge between the 
                ephemeral and the eternal, between presence and preservation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCK 3 - MISSION & VISION */}
      <section className="py-24 lg:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-cyan-500/30 hover:border-cyan-500/60 transition-all duration-300 reveal-on-scroll lift-on-hover neon-border-hover">
              <CardContent className="p-12 space-y-6">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl flex items-center justify-center energy-pulse">
                  <Target className="text-cyan-400" size={32} />
                </div>
                <h3 className="text-3xl font-bold shimmer">Mission</h3>
                <p className="text-xl text-gray-300 leading-relaxed">
                  To create digital presence systems that authentically reflect human identity.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-cyan-500/30 hover:border-cyan-500/60 transition-all duration-300 reveal-on-scroll lift-on-hover neon-border-hover" style={{ transitionDelay: '100ms' }}>
              <CardContent className="p-12 space-y-6">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl flex items-center justify-center energy-pulse">
                  <Eye className="text-cyan-400" size={32} />
                </div>
                <h3 className="text-3xl font-bold shimmer">Vision</h3>
                <p className="text-xl text-gray-300 leading-relaxed">
                  To ensure that memories, values, and personal depth can be shared and continued beyond time.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* BLOCK 4 - ETHICS STATEMENT */}
      <section className="py-24 lg:py-32 bg-[#0B1F2E]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-cyan-500/40 relative overflow-hidden reveal-on-scroll neon-border-hover">
            <div className="absolute inset-0 border-2 border-cyan-500/20 rounded-lg border-draw" />
            <CardContent className="p-16 text-center space-y-8 relative z-10">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl mb-4 energy-pulse">
                <Shield className="text-cyan-400 animate-glow-pulse" size={40} />
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold animate-letter-spacing">Ethical by Design</h2>
              <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
                RepresenX is built on transparency, consent, and respect. Your presence acts 
                only within boundaries you define — never beyond them. We believe ethical 
                technology starts with respecting autonomy, protecting privacy, and ensuring 
                that every digital presence remains under the complete control of its creator.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
